import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { solicitudDetalleService } from '../../services';

const paisesOptions = [
  'ARGENTINA',
  'BRASIL',
  'CHILE',
  'URUGUAY',
  'PARAGUAY',
  'BOLIVIA',
  'PERU',
  'COLOMBIA',
  'VENEZUELA',
  'ECUADOR',
  'MEXICO',
  'ESPAÑA',
  'ITALIA',
  'FRANCIA',
  'ALEMANIA',
  'ESTADOS UNIDOS',
  'CANADA',
  'REINO UNIDO',
  'REPUBLICA DE COREA',
  'OTRO'
];

const parseBackendFechaCierre = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Try parsing as YYYY-MM
    const parts = value.split('-');
    if (parts.length >= 2) {
      return `${parts[0]}-${parts[1]}`;
    }
    return '';
  }
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const buildBackendFechaCierre = (value) => {
  if (!value) return '';
  const [year, month] = value.split('-');
  if (!year || !month) return '';
  return `${year}-${month}-01`;
};

const SeccionDatosOrganizacionEmpresa = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    fechaConstitucion: '',
    numeroActa: '',
    paisOrigen: 'ARGENTINA',
    paisResidencia: 'ARGENTINA',
    fechaCierreBalance: ''
  });

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDatosOrganizacionEmpresa(solicitudId);
      if (response) {
        setFormData({
          fechaConstitucion: response.fechaConstitucion || '',
          numeroActa: response.numeroActa || '',
          paisOrigen: response.paisOrigen || 'ARGENTINA',
          paisResidencia: response.paisResidencia || 'ARGENTINA',
          fechaCierreBalance: parseBackendFechaCierre(response.fechaCierreBalance)
        });
      }
    } catch (err) {
      setError('Error al cargar los datos de organización');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        fechaConstitucion: formData.fechaConstitucion,
        numeroActa: formData.numeroActa,
        paisOrigen: formData.paisOrigen,
        paisResidencia: formData.paisResidencia,
        fechaCierreBalance: buildBackendFechaCierre(formData.fechaCierreBalance)
      };
      await solicitudDetalleService.saveDatosOrganizacionEmpresa(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Datos de Organización
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                type="date"
                label="Fecha de Constitución"
                value={formData.fechaConstitucion}
                onChange={(e) => handleInputChange('fechaConstitucion', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Número de Acta"
                value={formData.numeroActa}
                onChange={(e) => handleInputChange('numeroActa', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>País de Origen</InputLabel>
                <Select
                  value={formData.paisOrigen}
                  onChange={(e) => handleInputChange('paisOrigen', e.target.value)}
                  label="País de Origen"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>País de Residencia</InputLabel>
                <Select
                  value={formData.paisResidencia}
                  onChange={(e) => handleInputChange('paisResidencia', e.target.value)}
                  label="País de Residencia"
                >
                  {paisesOptions.map((pais) => (
                    <MenuItem key={pais} value={pais}>
                      {pais}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                type="month"
                label="Fecha de Cierre de Balance"
                value={formData.fechaCierreBalance}
                onChange={(e) => handleInputChange('fechaCierreBalance', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDatosOrganizacionEmpresa;
